import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getCustomers, getProducts, getSaleById, getSaleItems,
  getInstallmentsBySaleId, getDeliveryBySaleId,
  updateSaleTransaction, searchProfiloProductsByModelCode, getCustomerOpenNotes,
} from '@/lib/supabaseDb';
import type { ProfiloProductResult } from '@/lib/supabaseDb';
import CustomerFormModal from '@/components/feature/CustomerFormModal';
import CustomerSearchSelect from '@/components/feature/CustomerSearchSelect';
import CurrencyInput from '@/components/base/CurrencyInput';
import CityDistrictSelect from '@/components/base/CityDistrictSelect';
import { useDebounce } from '@/hooks/useDebounce';
import { formatTRY } from '@/lib/currencyFormat';

type ItemCategory = 'furniture' | 'profilo' | 'other';

const CATEGORY_LABELS: Record<ItemCategory, string> = { furniture: 'Mobilya', profilo: 'Profilo', other: 'Diğer' };

const furnitureTypes = ['Koltuk Takımı', 'Yatak Odası', 'Yemek Odası', 'TV Ünitesi', 'Genç Odası', 'Berjer', 'Sehpa', 'Vitrin', 'Kitaplık'];
const colors = ['Beyaz', 'Krem', 'Gri', 'Antrasit', 'Ceviz', 'Meşe', 'Maun', 'Siyah', 'Inox', 'Silver'];
const paymentTypes = ['Peşin', 'Taksitli', 'Senet', 'Kredi Kartı', 'Havale', 'Nakit'];

function createEmptyItem(category: ItemCategory) {
  const brand = category === 'furniture' ? 'Polatlar Mobilya' : category === 'profilo' ? 'Profilo' : '';
  return {
    productId: '',
    productName: '',
    productType: '',
    category,
    brand,
    modelCode: '',
    serialNumber: '',
    color: '',
    size: '',
    quantity: 1,
    unitPrice: 0,
    isManualProduct: true,
    unitCostPrice: null as number | null,
  };
}

function mapCategory(catLabel: string): ItemCategory {
  if (catLabel === 'Profilo') return 'profilo';
  if (catLabel === 'Diğer') return 'other';
  return 'furniture';
}

export default function EditSalePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [sale, setSale] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState<string | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const itemListRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({
    customerId: '', saleDate: '',
    paymentType: 'Taksitli', installmentCount: 3, firstPaymentDate: '',
    deliveryAddress: '', warrantyPeriod: '2 Yıl', notes: '', discountAmount: 0,
  });

  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryStartTime, setDeliveryStartTime] = useState('');
  const [deliveryEndTime, setDeliveryEndTime] = useState('');
  const [deliveryPerson, setDeliveryPerson] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [deliveryDistrict, setDeliveryDistrict] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryNeighborhood, setDeliveryNeighborhood] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliveryFromDb, setDeliveryFromDb] = useState<any>(null);

  const [items, setItems] = useState([createEmptyItem('furniture')]);
  const [itemSearch, setItemSearch] = useState<{ activeIdx: number | null; term: string }>({ activeIdx: null, term: '' });

  const [profiloModelSearch, setProfiloModelSearch] = useState<{ activeIdx: number | null; term: string }>({ activeIdx: null, term: '' });
  const debouncedProfiloSearch = useDebounce(profiloModelSearch.term, 400);
  const [profiloSearchResults, setProfiloSearchResults] = useState<Record<number, ProfiloProductResult[]>>({});
  const [profiloSearchLoading, setProfiloSearchLoading] = useState<Record<number, boolean>>({});
  const [profiloNotFound, setProfiloNotFound] = useState<Record<number, boolean>>({});
  const [profiloSearchDropdown, setProfiloSearchDropdown] = useState<number | null>(null);

  const [downPayment, setDownPayment] = useState(0);
  const [calculatedPaid, setCalculatedPaid] = useState(0);
  const [useManualTotal, setUseManualTotal] = useState(false);
  const [manualTotal, setManualTotal] = useState(0);

  const [installments, setInstallments] = useState<any[]>([]);
  const [hasOpenInstallments, setHasOpenInstallments] = useState(false);
  const [hasPaidInstallments, setHasPaidInstallments] = useState(false);
  const [installmentStrategy, setInstallmentStrategy] = useState<'last_installment' | 'distribute_evenly' | 'recreate'>('last_installment');

  const [promissoryNotes, setPromissoryNotes] = useState<any[]>([]);

  const [showChangeSummary, setShowChangeSummary] = useState(false);
  const [editReason, setEditReason] = useState('');

  // Installment plan state (mirrors new sale page)
  const [installmentPlanType, setInstallmentPlanType] = useState<'equal' | 'custom'>('equal');
  const [customInstallments, setCustomInstallments] = useState<Array<{ id: string; sequence_no: number; due_date: string; amount: number; description: string }>>([]);
  const [showGroupAdd, setShowGroupAdd] = useState(false);
  const [groupForm, setGroupForm] = useState({ months: 1, monthlyAmount: 0, firstDueDate: '', interval: 'Aylık' as 'Aylık' | '15 Gün' | 'Haftalık' | 'Özel' });

  // ── Load all data ──
  useEffect(() => {
    if (!id) return;
    async function load() {
      setLoading(true);
      const [custRes, prodRes, saleRes, itemsRes, instRes, delRes, notesRes] = await Promise.all([
        getCustomers(), getProducts(), getSaleById(id!), getSaleItems(id!),
        getInstallmentsBySaleId(id!), getDeliveryBySaleId(id!),
        import('@/lib/supabaseDb').then(m => m.getPromissoryNotes()),
      ]);

      setCustomers(custRes.data || []);
      setProducts(prodRes.data || []);

      const saleData = saleRes.data;
      setSale(saleData);

      const siData = itemsRes.data || [];
      const instData = instRes.data || [];
      const delData = delRes.data || null;
      const allNotes = notesRes.data || [];

      setInstallments(instData);
      setDeliveryFromDb(delData);
      setPromissoryNotes(allNotes.filter((n: any) => n.sale_id === id));

      const hasOpen = instData.some((i: any) => i.status !== 'Ödendi' && i.status !== 'İptal' && (i.remaining_amount || 0) > 0);
      const hasPaid = instData.some((i: any) => (i.paid_amount || 0) > 0);
      setHasOpenInstallments(hasOpen);
      setHasPaidInstallments(hasPaid);

      if (saleData && !initialized) {
        setForm({
          customerId: saleData.customer_id || '',
          saleDate: saleData.sale_date || '',
          paymentType: saleData.payment_type || 'Taksitli',
          installmentCount: saleData.installment_count || 3,
          firstPaymentDate: saleData.first_payment_date || '',
          deliveryAddress: saleData.delivery_address || '',
          warrantyPeriod: saleData.warranty_period || '2 Yıl',
          notes: saleData.notes || '',
          discountAmount: saleData.discount_amount || 0,
        });
        setDownPayment(saleData.down_payment || 0);
        setCalculatedPaid(saleData.paid_amount || saleData.down_payment || 0);

        // Map sale items
        if (siData.length > 0) {
          const mappedItems = siData.map((si: any) => ({
            productId: si.product_id || '',
            productName: si.product_name || '',
            productType: si.product_type || '',
            category: mapCategory(si.category || 'Mobilya'),
            brand: si.brand || '',
            modelCode: si.model_code || '',
            serialNumber: si.serial_number || '',
            color: si.color || '',
            size: si.size || '',
            quantity: Number(si.quantity) || 1,
            unitPrice: Number(si.unit_price) || 0,
            isManualProduct: si.is_manual !== false,
            unitCostPrice: si.unit_cost_price != null ? Number(si.unit_cost_price) : null,
          }));
          setItems(mappedItems);

          // Check if manual total was used
          const hasManualPricing = mappedItems.some((item: any) => item.productName && item.unitPrice === 0);
          if (hasManualPricing) {
            setUseManualTotal(true);
            setManualTotal(saleData.subtotal || saleData.total_amount || 0);
          }
        }

        // Delivery
        if (delData) {
          setDeliveryDate(delData.delivery_date || '');
          setDeliveryStartTime(delData.delivery_start_time || '');
          setDeliveryEndTime(delData.delivery_end_time || '');
          setDeliveryPerson(delData.delivery_person || '');
          setVehiclePlate(delData.vehicle_plate || '');
          setDeliveryDistrict(delData.district || '');
          setDeliveryCity(delData.city || '');
          setDeliveryNeighborhood(delData.neighborhood || '');
          setDeliveryNotes(delData.notes || '');
        }
      }
      setInitialized(true);
      setLoading(false);
    }
    load();
  }, [id]);

  // ── Profilo model code debounced arama ──
  useEffect(() => {
    const idx = profiloModelSearch.activeIdx;
    const term = debouncedProfiloSearch;
    if (idx === null || !term || term.length < 2) return;
    let cancelled = false;
    setProfiloSearchLoading(prev => ({ ...prev, [idx]: true }));
    setProfiloNotFound(prev => ({ ...prev, [idx]: false }));
    searchProfiloProductsByModelCode(term).then(res => {
      if (cancelled) return;
      setProfiloSearchLoading(prev => ({ ...prev, [idx]: false }));
      const results = res.data || [];
      setProfiloSearchResults(prev => ({ ...prev, [idx]: results }));
      if (results.length === 0) setProfiloNotFound(prev => ({ ...prev, [idx]: true }));
      setProfiloSearchDropdown(idx);
    }).catch(() => {
      if (cancelled) return;
      setProfiloSearchLoading(prev => ({ ...prev, [idx]: false }));
    });
    return () => { cancelled = true; };
  }, [debouncedProfiloSearch, profiloModelSearch.activeIdx]);

  // ── Installment plan helpers ──
  const addCustomRow = () => {
    const seq = customInstallments.length + 1;
    const lastDate = customInstallments.length > 0 ? customInstallments[customInstallments.length - 1].due_date : (form.firstPaymentDate || form.saleDate);
    const nextDate = new Date(lastDate);
    nextDate.setMonth(nextDate.getMonth() + 1);
    const lastDay = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
    if (nextDate.getDate() > lastDay) nextDate.setDate(lastDay);
    setCustomInstallments([...customInstallments, { id: crypto.randomUUID(), sequence_no: seq, due_date: nextDate.toISOString().split('T')[0], amount: 0, description: '' }]);
  };

  const updateCustomRow = (rowId: string, field: string, value: string | number) => {
    setCustomInstallments(prev => prev.map(row => row.id === rowId ? { ...row, [field]: field === 'amount' ? (parseFloat(value as string) || 0) : value } : row));
  };

  const removeCustomRow = (rowId: string) => {
    setCustomInstallments(prev => prev.filter(r => r.id !== rowId).map((r, i) => ({ ...r, sequence_no: i + 1 })));
  };

  const distributeEqually = () => {
    if (customInstallments.length === 0 || targetAmount <= 0) return;
    const count = customInstallments.length;
    const base = Math.floor(targetAmount / count);
    const remainder = targetAmount - base * count;
    const updated = customInstallments.map((row, i) => ({ ...row, amount: i === count - 1 ? base + remainder : base }));
    setCustomInstallments(updated);
  };

  const clearAmounts = () => setCustomInstallments(prev => prev.map(r => ({ ...r, amount: 0 })));
  const resetPlan = () => setCustomInstallments([]);
  const copyPreviousRow = () => {
    if (customInstallments.length === 0) return;
    const last = customInstallments[customInstallments.length - 1];
    const nextDate = new Date(last.due_date);
    nextDate.setMonth(nextDate.getMonth() + 1);
    const lastDay = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
    if (nextDate.getDate() > lastDay) nextDate.setDate(lastDay);
    setCustomInstallments([...customInstallments, { id: crypto.randomUUID(), sequence_no: customInstallments.length + 1, due_date: nextDate.toISOString().split('T')[0], amount: last.amount, description: last.description }]);
  };

  const generateDates = () => {
    if (customInstallments.length === 0) return;
    const firstDate = customInstallments[0].due_date ? new Date(customInstallments[0].due_date) : new Date(form.firstPaymentDate || form.saleDate);
    const baseDay = firstDate.getDate();
    const updated = customInstallments.map((row, i) => {
      const d = new Date(firstDate);
      d.setMonth(d.getMonth() + i);
      const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      d.setDate(Math.min(baseDay, lastDay));
      return { ...row, due_date: d.toISOString().split('T')[0] };
    });
    setCustomInstallments(updated);
  };

  const addDiffToLast = () => {
    if (customInstallments.length === 0) return;
    const updated = [...customInstallments];
    const last = updated[updated.length - 1];
    updated[updated.length - 1] = { ...last, amount: Math.max(0, (last.amount || 0) + customDiff) };
    setCustomInstallments(updated);
  };

  const handleGroupAdd = () => {
    if (groupForm.months < 1 || groupForm.monthlyAmount <= 0 || !groupForm.firstDueDate) return;
    const newRows: typeof customInstallments = [];
    const startDate = new Date(groupForm.firstDueDate);
    const baseDay = startDate.getDate();
    for (let i = 0; i < groupForm.months; i++) {
      const dueDate = new Date(startDate);
      if (groupForm.interval === 'Aylık') dueDate.setMonth(dueDate.getMonth() + i);
      else if (groupForm.interval === '15 Gün') dueDate.setDate(dueDate.getDate() + i * 15);
      else if (groupForm.interval === 'Haftalık') dueDate.setDate(dueDate.getDate() + i * 7);
      const lastDay = new Date(dueDate.getFullYear(), dueDate.getMonth() + 1, 0).getDate();
      if (groupForm.interval === 'Aylık' && dueDate.getDate() !== baseDay) dueDate.setDate(Math.min(baseDay, lastDay));
      newRows.push({ id: crypto.randomUUID(), sequence_no: customInstallments.length + i + 1, due_date: dueDate.toISOString().split('T')[0], amount: groupForm.monthlyAmount, description: '' });
    }
    setCustomInstallments([...customInstallments, ...newRows]);
    setShowGroupAdd(false);
    setGroupForm({ months: 1, monthlyAmount: 0, firstDueDate: '', interval: 'Aylık' });
  };

  // ── Item helpers ──
  const addItem = (category: ItemCategory) => {
    const newItem = createEmptyItem(category);
    setItems(prev => [newItem, ...prev]);
    setShowCategoryPicker(false);
    setTimeout(() => { itemListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 50);
  };

  const removeItem = (idx: number) => { if (items.length > 1) setItems(items.filter((_, i) => i !== idx)); };
  const updateItem = (idx: number, field: string, value: string | number | boolean) => { const newItems = [...items]; (newItems[idx] as any)[field] = value; setItems(newItems); };

  const getProductsByCategory = (category: ItemCategory) => {
    const catLabel = CATEGORY_LABELS[category];
    return products.filter((p: any) => p.category === catLabel);
  };

  const handleProductSelect = (idx: number, productId: string) => {
    if (!productId) {
      updateItem(idx, 'productId', ''); updateItem(idx, 'productName', '');
      updateItem(idx, 'isManualProduct', true); updateItem(idx, 'unitCostPrice', null);
      updateItem(idx, 'modelCode', ''); updateItem(idx, 'serialNumber', '');
      updateItem(idx, 'color', ''); updateItem(idx, 'size', '');
      return;
    }
    const prod = products.find((p: any) => p.id === productId);
    if (!prod) return;
    updateItem(idx, 'productId', prod.id);
    updateItem(idx, 'productName', prod.name || '');
    updateItem(idx, 'productType', prod.product_type || prod.type || '');
    updateItem(idx, 'brand', prod.brand || '');
    updateItem(idx, 'modelCode', prod.model_code || '');
    updateItem(idx, 'serialNumber', prod.serial_number || '');
    updateItem(idx, 'color', prod.color || '');
    updateItem(idx, 'size', prod.size || '');
    updateItem(idx, 'unitPrice', prod.sale_price || 0);
    updateItem(idx, 'isManualProduct', false);
    updateItem(idx, 'unitCostPrice', prod.purchase_price || null);
  };

  const handleProfiloProductSelect = (idx: number, prod: ProfiloProductResult) => {
    updateItem(idx, 'productId', prod.id);
    updateItem(idx, 'productName', prod.name || '');
    updateItem(idx, 'productType', prod.product_type || '');
    updateItem(idx, 'brand', prod.brand || 'Profilo');
    updateItem(idx, 'modelCode', prod.model_code || '');
    updateItem(idx, 'color', prod.color || '');
    updateItem(idx, 'size', prod.capacity || prod.size || '');
    updateItem(idx, 'serialNumber', '');
    updateItem(idx, 'isManualProduct', false);
    updateItem(idx, 'unitPrice', prod.sale_price || 0);
    updateItem(idx, 'unitCostPrice', prod.purchase_price || null);
    setProfiloModelSearch({ activeIdx: null, term: '' });
    setProfiloSearchDropdown(null);
    setProfiloNotFound(prev => ({ ...prev, [idx]: false }));
    setProfiloSearchResults(prev => ({ ...prev, [idx]: [] }));
  };

  const handleProfiloManualContinue = (idx: number) => {
    updateItem(idx, 'modelCode', profiloModelSearch.term || '');
    updateItem(idx, 'productId', '');
    updateItem(idx, 'isManualProduct', true);
    updateItem(idx, 'productName', '');
    updateItem(idx, 'unitCostPrice', null);
    setProfiloModelSearch({ activeIdx: null, term: '' });
    setProfiloSearchDropdown(null);
    setProfiloNotFound(prev => ({ ...prev, [idx]: false }));
  };

  const getFilteredProducts = (idx: number, searchTerm: string) => {
    const item = items[idx];
    if (!item) return [];
    const catProducts = getProductsByCategory(item.category);
    if (!searchTerm.trim()) return catProducts;
    const term = searchTerm.toLowerCase();
    return catProducts.filter((p: any) =>
      (p.name || '').toLowerCase().includes(term) ||
      (p.brand || '').toLowerCase().includes(term) ||
      (p.model_code || '').toLowerCase().includes(term) ||
      (p.product_type || p.type || '').toLowerCase().includes(term)
    );
  };

  const getProductById = (productId: string) => products.find((p: any) => p.id === productId) || null;

  const handleCustomerCreated = async (customerId: string) => {
    setShowCustomerModal(false);
    setForm(prev => ({ ...prev, customerId }));
    const custRes = await getCustomers();
    const custList = custRes.data || [];
    setCustomers(custList);
    const c = custList.find((cust: any) => cust.id === customerId);
    if (c) {
      setForm(prev => ({ ...prev, deliveryAddress: `${c.address || ''} ${c.district || ''} / ${c.city || ''}`, customerId }));
      if (!deliveryDistrict) setDeliveryDistrict(c.district || '');
      if (!deliveryCity) setDeliveryCity(c.city || '');
      if (!deliveryNeighborhood) setDeliveryNeighborhood(c.neighborhood || '');
    }
  };

  // ── Calculations ──
  const subtotal = items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const effectiveSubtotal = useManualTotal && manualTotal > 0 ? manualTotal : subtotal;
  const totalAmount = effectiveSubtotal - form.discountAmount;
  const remainingAmount = Math.max(0, totalAmount - calculatedPaid);

  const oldTotalAmount = sale?.total_amount || 0;
  const oldRemainingAmount = sale?.remaining_amount || 0;
  const totalAmountDelta = totalAmount - oldTotalAmount;
  const priceChanged = totalAmountDelta !== 0 || (sale?.subtotal || 0) !== subtotal;
  const newTotalTooLow = priceChanged && totalAmount < calculatedPaid;
  const oldTotalCost = (sale?.sale_items || []).reduce ? 0 : 0;
  const newTotalCost = items.reduce((sum, i) => sum + (i.unitCostPrice || 0) * i.quantity, 0);

  const customTotal = customInstallments.reduce((s, i) => s + i.amount, 0);
  const targetAmount = remainingAmount;
  const customDiff = targetAmount - customTotal;
  const customIsBalanced = Math.abs(customDiff) < 0.01;

  const selectedCustomer = customers.find(c => c.id === form.customerId);

  // Profit calculation
  const profitItems = items.map((item) => {
    const revenue = item.quantity * item.unitPrice;
    const hasCost = item.unitCostPrice != null && item.unitCostPrice > 0;
    const cost = hasCost ? item.quantity * item.unitCostPrice : 0;
    const profit = hasCost ? revenue - cost : null;
    const profitRate = hasCost && revenue > 0 ? ((profit! / revenue) * 100) : null;
    return { productName: item.productName || `Ürün ${items.indexOf(item) + 1}`, revenue, cost, profit, profitRate, hasCost };
  });
  const totalCostAll = profitItems.reduce((s, p) => s + p.cost, 0);
  const totalKnownRevenue = profitItems.filter(p => p.hasCost).reduce((s, p) => s + p.revenue, 0);
  const estimatedProfit = profitItems.filter(p => p.hasCost).reduce((s, p) => s + (p.profit || 0), 0);
  const unknownCostRevenue = profitItems.filter(p => !p.hasCost).reduce((s, p) => s + p.revenue, 0);
  const estimatedProfitRate = totalKnownRevenue > 0 ? ((estimatedProfit / totalKnownRevenue) * 100) : null;
  const hasAnyCost = profitItems.some(p => p.hasCost);
  const effectiveProfit = useManualTotal && manualTotal > 0 ? manualTotal - totalCostAll : estimatedProfit;
  const effectiveProfitRate = useManualTotal && manualTotal > 0 && manualTotal > 0 ? ((effectiveProfit / manualTotal) * 100) : estimatedProfitRate;

  // ── Review & Submit ──
  const handleReviewChanges = () => {
    if (!form.customerId) { setShowToast('Lütfen müşteri seçin'); setTimeout(() => setShowToast(null), 3000); return; }
    if (useManualTotal && manualTotal <= 0) { setShowToast('Lütfen manuel toplam tutarını giriniz.'); setTimeout(() => setShowToast(null), 3000); return; }
    if (!useManualTotal && items.some(i => !i.productName || i.unitPrice <= 0)) { setShowToast('Lütfen tüm ürün bilgilerini doldurun (ürün adı, birim fiyat zorunludur)'); setTimeout(() => setShowToast(null), 3000); return; }
    if (items.some(i => i.category === 'profilo' && !i.productId && i.productName)) { setShowToast('Profilo ürünlerinde sistemde kayıtlı ürün seçimi zorunludur.'); setTimeout(() => setShowToast(null), 3000); return; }
    if (!deliveryDate || !deliveryStartTime) { setShowToast('Lütfen teslimat tarihi ve teslimat saatini giriniz.'); setTimeout(() => setShowToast(null), 3000); return; }
    if (newTotalTooLow) { setShowToast('Yeni satış toplamı, tahsil edilmiş tutardan düşük olamaz. Önce ilgili ödeme için iptal/iade işlemi yapmalısınız.'); setTimeout(() => setShowToast(null), 5000); return; }
    if (form.paymentType === 'Taksitli' && installmentPlanType === 'custom') {
      if (customInstallments.length === 0) { setShowToast('Lütfen özel taksit planına en az bir taksit ekleyin.'); setTimeout(() => setShowToast(null), 3000); return; }
      if (customInstallments.some(r => r.amount <= 0)) { setShowToast('Tüm taksit tutarları 0\'dan büyük olmalıdır.'); setTimeout(() => setShowToast(null), 3000); return; }
      if (customInstallments.some(r => !r.due_date)) { setShowToast('Tüm taksitler için vade tarihi giriniz.'); setTimeout(() => setShowToast(null), 3000); return; }
      if (!customIsBalanced) { setShowToast(`Taksit planı toplamı (${formatTRY(customTotal)}) ile kalan borç (${formatTRY(targetAmount)}) eşleşmiyor. Fark: ${formatTRY(Math.abs(customDiff))}`); setTimeout(() => setShowToast(null), 4000); return; }
    }
    setShowChangeSummary(true);
  };

  const handleSubmit = async () => {
    if (!sale || saving) return;
    if (!editReason.trim()) { setShowToast('Lütfen değişiklik sebebini giriniz.'); setTimeout(() => setShowToast(null), 3000); return; }

    setSaving(true);
    try {
      const saleItemsPayload = items.map(item => ({
        product_id: item.productId || null,
        product_name: item.productName,
        product_type: item.productType,
        brand: item.brand,
        model_code: item.modelCode || null,
        color: item.color || null,
        size: item.size || null,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        unit_cost_price: item.unitCostPrice,
        is_manual: item.isManualProduct,
        serial_number: item.serialNumber || null,
        category: CATEGORY_LABELS[item.category],
      }));

      const deliveryData = deliveryFromDb ? {
        delivery_date: deliveryDate,
        delivery_start_time: deliveryStartTime,
        delivery_end_time: deliveryEndTime,
        delivery_person: deliveryPerson,
        vehicle_plate: vehiclePlate,
        district: deliveryDistrict || selectedCustomer?.district || '',
        city: deliveryCity || selectedCustomer?.city || '',
        neighborhood: deliveryNeighborhood || selectedCustomer?.neighborhood || '',
        address: form.deliveryAddress,
        notes: deliveryNotes,
      } : null;

      const { data: result, error: rpcErr } = await updateSaleTransaction({
        sale_id: sale.id,
        sale_data: {
          customer_id: form.customerId,
          customer_name: selectedCustomer?.full_name || '',
          sale_date: form.saleDate,
          payment_type: form.paymentType,
          discount_amount: form.discountAmount,
          subtotal: effectiveSubtotal,
          total_amount: totalAmount,
          down_payment: downPayment,
          paid_amount: calculatedPaid,
          remaining_amount: remainingAmount,
          installment_count: form.paymentType === 'Taksitli' ? form.installmentCount : 0,
          first_payment_date: form.firstPaymentDate || null,
          delivery_address: form.deliveryAddress,
          warranty_period: form.warrantyPeriod,
          notes: form.notes,
          payment_status: remainingAmount <= 0 ? 'Ödendi' : calculatedPaid > 0 ? 'Kısmi Ödendi' : 'Borçlu',
        },
        sale_items: saleItemsPayload,
        reason: editReason,
        installment_strategy: hasOpenInstallments ? installmentStrategy : undefined,
        delivery_data: deliveryData,
      });

      if (rpcErr || !result || !result.success) {
        setShowToast(rpcErr || 'Satış güncellenirken bir hata oluştu.');
        setTimeout(() => setShowToast(null), 4000);
        setSaving(false);
        return;
      }

      setShowToast('Satış başarıyla güncellendi.');
      setTimeout(() => { setShowToast(null); navigate(`/sales/${sale.id}`); }, 1500);
    } catch {
      setShowToast('Satış güncellenirken bir hata oluştu.');
      setTimeout(() => setShowToast(null), 3000);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──
  if (loading) return <div className="flex flex-col items-center justify-center h-[60vh]"><div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4"><div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div></div></div>;

  if (!sale) return <div className="flex flex-col items-center justify-center h-[60vh]"><div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4"><i className="ri-file-unknow-line text-2xl text-foreground-300"></i></div><h2 className="text-lg font-semibold text-foreground-700">Satış bulunamadı</h2><button onClick={() => navigate('/sales')} className="btn-primary mt-4 cursor-pointer">Listeye Dön</button></div>;

  const categoryBadgeColor = (cat: ItemCategory) => {
    if (cat === 'furniture') return 'bg-amber-100 text-amber-700 border-amber-200';
    if (cat === 'profilo') return 'bg-sky-100 text-sky-700 border-sky-200';
    return 'bg-foreground-100 text-foreground-600 border-foreground-200';
  };

  return (
    <div className="space-y-5 max-w-3xl mx-auto">
      {showToast && <div className={`fixed top-4 right-4 z-[100] px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 ${showToast.includes('başarıyla') || showToast.includes('güncellendi') ? 'bg-emerald-500' : 'bg-amber-500'} text-white`}><i className={showToast.includes('başarıyla') || showToast.includes('güncellendi') ? 'ri-check-line' : 'ri-error-warning-line'}></i><span className="text-sm font-medium">{showToast}</span></div>}

      <div className="flex items-center gap-3">
        <button onClick={() => navigate(`/sales/${sale.id}`)} className="w-9 h-9 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-arrow-left-line text-foreground-600"></i></button>
        <div><h2 className="text-xl font-bold text-foreground-900 font-heading">Satışı Düzenle</h2><p className="text-sm text-foreground-400">Satış No: {sale.sale_no}</p></div>
      </div>

      {/* Bölüm 1: Müşteri + Tarih */}
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-5 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-foreground-500 mb-1.5">Müşteri <span className="text-red-400">*</span></label>
            <div className="flex gap-2">
              <div className="flex-1">
                <CustomerSearchSelect value={form.customerId} onChange={(id, customer) => { setForm({ ...form, customerId: id }); if (id && customer) { const c = customer; setForm(f => ({ ...f, deliveryAddress: `${c.address || ''} ${c.district || ''} / ${c.city || ''}`, customerId: id })); if (!deliveryDistrict) setDeliveryDistrict(c.district || ''); if (!deliveryCity) setDeliveryCity(c.city || ''); if (!deliveryNeighborhood) setDeliveryNeighborhood(c.neighborhood || ''); if (!customers.find(x => x.id === c.id)) { setCustomers(prev => [...prev, c]); } } }} placeholder="Müşteri ara..." />
              </div>
              <button onClick={() => setShowCustomerModal(true)} className="btn-outline px-3 py-2 text-sm cursor-pointer whitespace-nowrap"><i className="ri-user-add-line mr-1"></i>Yeni</button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-500 mb-1.5">Satış Tarihi</label>
            <input type="date" value={form.saleDate} onChange={e => setForm({ ...form, saleDate: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" />
          </div>
        </div>
        {selectedCustomer && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
            <span className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0"><i className="ri-user-line text-background-50 text-sm"></i></span>
            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-primary-800">{selectedCustomer.full_name}</p><p className="text-xs text-primary-600">{selectedCustomer.phone} · {selectedCustomer.district}</p></div>
            <span className={`text-xs px-2 py-1 rounded-full ${selectedCustomer.total_debt_amount > 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>Borç: {formatTRY(selectedCustomer.total_debt_amount)}</span>
          </div>
        )}

        {/* Bölüm 2: Ürünler */}
        <div ref={itemListRef}>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-medium text-foreground-500">Ürünler <span className="text-red-400">*</span></label>
            <button onClick={() => setShowCategoryPicker(true)} className="text-xs text-primary-500 hover:text-primary-600 font-medium cursor-pointer flex items-center gap-1">
              <i className="ri-add-line"></i> Ürün Ekle
            </button>
          </div>
          <div className="space-y-3">
            {items.map((item, idx) => {
              const selectedProd = item.productId ? getProductById(item.productId) : null;
              const isFurniture = item.category === 'furniture';
              const isProfilo = item.category === 'profilo';
              const isOther = item.category === 'other';
              const catProducts = getProductsByCategory(item.category);
              const filteredProducts = getFilteredProducts(idx, itemSearch.activeIdx === idx ? itemSearch.term : '');
              const isSearching = itemSearch.activeIdx === idx;

              return (
                <div key={`${idx}-${item.category}`} className="bg-background-100/50 rounded-xl p-4 border border-background-200/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-foreground-500">Ürün {idx + 1}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${categoryBadgeColor(item.category)}`}>
                        {CATEGORY_LABELS[item.category]}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {!item.isManualProduct && selectedProd && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          selectedProd.stock_quantity <= 0 ? 'bg-red-100 text-red-700' :
                          selectedProd.stock_quantity <= (selectedProd.critical_stock_level || 1) ? 'bg-amber-100 text-amber-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>Stok: {selectedProd.stock_quantity || 0} adet</span>
                      )}
                      {items.length > 1 && (
                        <button onClick={() => removeItem(idx)} className="w-6 h-6 rounded flex items-center justify-center cursor-pointer text-red-400 hover:bg-red-50">
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">

                    {!isFurniture && (
                      <div>
                        {isProfilo ? (
                          <select value={item.productType} onChange={e => updateItem(idx, 'productType', e.target.value)}
                            className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">
                            <option value="">Cins</option>
                            {['Buzdolabı', 'Çamaşır Makinesi', 'Bulaşık Makinesi', 'Fırın', 'Klima', 'Derin Dondurucu'].map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        ) : (
                          <input type="text" value={item.productType} onChange={e => updateItem(idx, 'productType', e.target.value)}
                            className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Ürün cinsi" />
                        )}
                      </div>
                    )}

                    {isOther ? (
                      <div><input type="text" value={item.brand} onChange={e => updateItem(idx, 'brand', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Marka" /></div>
                    ) : (
                      <div><div className="w-full px-2.5 py-2 text-sm bg-background-100/70 border border-background-200/70 rounded-lg text-foreground-500 font-medium">{item.brand || '—'}</div></div>
                    )}

                    <div className="relative">
                      {isProfilo ? (
                        <select value={item.productId} onChange={e => handleProductSelect(idx, e.target.value)}
                          className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">
                          <option value="">Ürün Seçiniz *</option>
                          {catProducts.filter((p: any) => p.status !== 'Stokta Yok' || (p.stock_quantity || 0) > 0).map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name} ({p.stock_quantity || 0} stok)</option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <input type="text"
                            value={isSearching ? itemSearch.term : (item.productName || '')}
                            onChange={e => { updateItem(idx, 'productName', e.target.value); updateItem(idx, 'isManualProduct', true); updateItem(idx, 'productId', ''); setItemSearch({ activeIdx: idx, term: e.target.value }); }}
                            onFocus={() => setItemSearch({ activeIdx: idx, term: item.productName || '' })}
                            onBlur={() => setTimeout(() => setItemSearch({ activeIdx: null, term: '' }), 200)}
                            className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700"
                            placeholder={isFurniture ? 'Ürün ara veya yaz...' : 'Ürün adı yaz...'} />
                          {isSearching && filteredProducts.length > 0 && (
                            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                              {filteredProducts.map((p: any) => (
                                <button key={p.id} type="button" onMouseDown={e => { e.preventDefault(); handleProductSelect(idx, p.id); setItemSearch({ activeIdx: null, term: '' }); }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 cursor-pointer flex items-center justify-between">
                                  <span className="text-foreground-700">{p.name}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${(p.stock_quantity || 0) > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{p.stock_quantity || 0}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {isProfilo ? (
                      <div className="relative">
                        <input type="text"
                          value={profiloModelSearch.activeIdx === idx ? profiloModelSearch.term : (item.modelCode || '')}
                          onChange={e => {
                            const val = e.target.value;
                            setProfiloModelSearch({ activeIdx: idx, term: val });
                            if (item.productId) { updateItem(idx, 'productId', ''); updateItem(idx, 'productName', ''); updateItem(idx, 'isManualProduct', true); }
                            updateItem(idx, 'modelCode', val);
                          }}
                          onFocus={() => { setProfiloModelSearch({ activeIdx: idx, term: item.modelCode || '' }); setProfiloSearchDropdown(idx); }}
                          onBlur={() => setTimeout(() => setProfiloSearchDropdown(null), 250)}
                          className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 pr-10"
                          placeholder="Model kod ara..." autoComplete="off" />
                        {profiloSearchLoading[idx] && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                        {profiloSearchDropdown === idx && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background-50 border border-background-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                            {profiloSearchLoading[idx] ? (
                              <div className="px-4 py-3 text-center text-sm text-foreground-400">Aranıyor...</div>
                            ) : profiloNotFound[idx] ? (
                              <div className="p-3">
                                <div className="flex items-start gap-2 text-sm">
                                  <i className="ri-information-line text-amber-500 mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center"></i>
                                  <div>
                                    <p className="text-amber-700 font-medium">Bu model koduyla kayıtlı Profilo ürünü bulunamadı.</p>
                                    <button type="button" onMouseDown={e => { e.preventDefault(); handleProfiloManualContinue(idx); }} className="mt-1 px-3 py-1.5 text-xs rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-medium cursor-pointer whitespace-nowrap">Manuel devam et</button>
                                  </div>
                                </div>
                              </div>
                            ) : (profiloSearchResults[idx] || []).length > 0 ? (
                              (profiloSearchResults[idx] || []).map((prod: ProfiloProductResult) => (
                                <button key={prod.id} type="button" onMouseDown={e => { e.preventDefault(); handleProfiloProductSelect(idx, prod); }} className="w-full text-left px-3 py-3 hover:bg-primary-50 cursor-pointer border-b border-background-100 last:border-0 transition-colors">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-sm font-semibold text-foreground-800">{prod.model_code}</span>
                                        <span className="text-xs text-foreground-400 truncate">{prod.name}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1.5 text-xs flex-wrap">
                                        {prod.product_type && <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-md text-[10px] font-medium">{prod.product_type}</span>}
                                        {prod.sale_price > 0 && <span className="text-emerald-600 font-medium">Satış: {formatTRY(prod.sale_price)}</span>}
                                        {prod.purchase_price > 0 && <span className="text-amber-600">Maliyet: {formatTRY(prod.purchase_price)}</span>}
                                      </div>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex-shrink-0 mt-0.5 ${(prod.stock_quantity || 0) > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                      Stok: {prod.stock_quantity || 0}
                                    </span>
                                  </div>
                                </button>
                              ))
                            ) : null}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div><input type="text" value={item.modelCode} onChange={e => updateItem(idx, 'modelCode', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Model kod" /></div>
                    )}

                    {isProfilo && (
                      <div><input type="text" value={item.serialNumber} onChange={e => updateItem(idx, 'serialNumber', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Seri no" /></div>
                    )}

                    <div>
                      <select value={item.color} onChange={e => updateItem(idx, 'color', e.target.value)}
                        className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">
                        <option value="">Renk</option>
                        {colors.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>

                    <div><input type="text" value={item.size} onChange={e => updateItem(idx, 'size', e.target.value)}
                      className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder={isOther ? 'Ölçü / açıklama' : 'Ölçü'} /></div>

                    <div><input type="number" min="1" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>

                    <div><CurrencyInput value={String(item.unitPrice || '')} onChange={v => updateItem(idx, 'unitPrice', parseFloat(v) || 0)}
                      className="w-full px-2.5 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Birim fiyat" /></div>

                    <div>
                      <CurrencyInput value={item.unitCostPrice != null ? String(item.unitCostPrice) : ''} onChange={v => updateItem(idx, 'unitCostPrice', parseFloat(v) || 0)}
                        className="w-full px-2.5 py-2 text-sm bg-amber-50 border border-amber-200 rounded-lg text-foreground-700" placeholder="Birim maliyet" />
                      <p className="text-[10px] text-foreground-400 mt-0.5">{item.unitCostPrice != null && item.unitCostPrice > 0 ? '' : 'Girilirse kâr hesaplanır'}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bölüm 3: Finansal Özet */}
        <div className="bg-background-100/50 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground-500">Ara Toplam</span>
            <span className={`font-medium ${useManualTotal ? 'text-foreground-300 line-through' : ''}`}>{formatTRY(subtotal)}</span>
          </div>
          <div className="border border-dashed border-amber-300 bg-amber-50/30 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="manualTotalToggle" checked={useManualTotal} onChange={e => { setUseManualTotal(e.target.checked); if (!e.target.checked) setManualTotal(0); }}
                className="w-4 h-4 rounded border-amber-300 text-amber-500 focus:ring-amber-400 cursor-pointer" />
              <label htmlFor="manualTotalToggle" className="text-sm font-medium text-amber-800 cursor-pointer whitespace-nowrap">Manuel Toplam Girişi</label>
            </div>
            {useManualTotal && (
              <>
                <p className="text-xs text-amber-600">Birim fiyat girmeden, satışın toplam tutarını elle girin. Kâr, toplamdan maliyetler çıkarılarak otomatik hesaplanır.</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground-700 whitespace-nowrap">Toplam Tutar</span>
                  <CurrencyInput value={String(manualTotal || '')} onChange={v => setManualTotal(parseFloat(v) || 0)}
                    className="flex-1 px-3 py-2.5 text-base font-semibold bg-amber-50 border-2 border-amber-300 rounded-lg text-foreground-800" placeholder="₺" />
                </div>
              </>
            )}
          </div>
          <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">İndirim</span><CurrencyInput value={String(form.discountAmount || '')} onChange={v => setForm({ ...form, discountAmount: parseFloat(v) || 0 })} className="w-24 px-2.5 py-1.5 text-sm text-right bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
          <div className="flex items-center justify-between text-base border-t border-background-200 pt-3">
            <span className="font-semibold">{useManualTotal && manualTotal > 0 ? 'Genel Toplam' : 'Toplam'}</span>
            <span className={`font-bold text-lg ${useManualTotal && manualTotal > 0 ? 'text-amber-700' : ''}`}>{formatTRY(totalAmount)}</span>
          </div>
          {newTotalTooLow && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2"><i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i><span>Yeni satış toplamı ({formatTRY(totalAmount)}), tahsil edilmiş tutardan ({formatTRY(calculatedPaid)}) düşük olamaz. Önce ilgili ödeme için iptal/iade işlemi yapmalısınız.</span></div>}
        </div>

        {/* Bölüm 4: Tahmini Kâr */}
        {hasAnyCost && (
          <div className="bg-emerald-50/40 border border-emerald-200/60 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center"><i className="ri-line-chart-line text-background-50 text-sm"></i></span>
              <h4 className="text-sm font-semibold text-emerald-800">Tahmini Kâr</h4>
            </div>
            {useManualTotal && manualTotal > 0 ? (
              <>
                <div className="space-y-1.5">
                  {profitItems.filter(p => p.hasCost).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-foreground-600 bg-background-50/70 rounded-lg px-3 py-2">
                      <span className="truncate max-w-[160px]">{p.productName}</span>
                      <span className="text-foreground-500">Maliyet: {formatTRY(p.cost)}</span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-emerald-200 pt-3 space-y-2">
                  <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">Toplam Maliyet</span><span className="font-medium text-foreground-700">{formatTRY(totalCostAll)}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">Manuel Toplam</span><span className="font-medium text-amber-700">{formatTRY(manualTotal)}</span></div>
                  <div className="flex items-center justify-between border-t border-emerald-200 pt-2">
                    <span className="text-sm font-medium text-emerald-800">Tahmini Kâr</span>
                    <span className={`text-lg font-bold font-heading ${effectiveProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {formatTRY(effectiveProfit)}
                      {effectiveProfitRate != null && <span className="text-xs ml-1 font-medium">(%{(effectiveProfitRate >= 0 ? '+' : '')}{Math.round(effectiveProfitRate)})</span>}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5">
                  {profitItems.filter(p => p.hasCost).map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs text-foreground-600 bg-background-50/70 rounded-lg px-3 py-2">
                      <span className="truncate max-w-[140px]">{p.productName}</span>
                      <span className="text-foreground-400">{formatTRY(p.cost)} → {formatTRY(p.revenue)}</span>
                      <span className={`font-semibold whitespace-nowrap ${(p.profit || 0) >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {formatTRY(p.profit || 0)}
                        {p.profitRate != null && <span className="text-[10px] ml-0.5">(%{(p.profitRate >= 0 ? '+' : '')}{Math.round(p.profitRate)})</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-emerald-200 pt-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-emerald-800">Toplam Tahmini Kâr</span>
                  <span className={`text-lg font-bold font-heading ${estimatedProfit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {formatTRY(estimatedProfit)}
                    {estimatedProfitRate != null && <span className="text-xs ml-1 font-medium">(%{(estimatedProfitRate >= 0 ? '+' : '')}{Math.round(estimatedProfitRate)})</span>}
                  </span>
                </div>
              </>
            )}
            {!useManualTotal && unknownCostRevenue > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-center gap-2 text-xs">
                <i className="ri-information-line text-amber-500"></i>
                <span className="text-amber-700">{formatTRY(unknownCostRevenue)} tutarında ürünün maliyeti girilmedi — bu ürünler kâr hesabına dahil edilmez.</span>
              </div>
            )}
          </div>
        )}
        {!hasAnyCost && !useManualTotal && items.some(i => i.productName && i.unitPrice > 0) && (
          <div className="bg-amber-50/40 border border-amber-200/60 rounded-xl p-4 flex items-center gap-3">
            <span className="w-7 h-7 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0"><i className="ri-information-line text-background-50 text-sm"></i></span>
            <div><p className="text-sm font-medium text-amber-800">Maliyet bilgisi eksik</p><p className="text-xs text-amber-600 mt-0.5">Ürünlerin birim maliyetini girin, kâr otomatik hesaplansın. Stoktan ürün seçerseniz alış fiyatı otomatik gelir.</p></div>
          </div>
        )}

        {/* Bölüm 5: Ödeme + Kalan */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Ödeme Tipi</label><select value={form.paymentType} onChange={e => setForm({ ...form, paymentType: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">{paymentTypes.map(p => <option key={p} value={p}>{p}</option>)}</select></div>
          <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Peşinat (TL)</label><CurrencyInput value={String(downPayment || '')} onChange={v => setDownPayment(parseFloat(v) || 0)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
          <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Ödenen</label><div className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg font-medium text-emerald-600">{formatTRY(calculatedPaid)}</div></div>
          <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Kalan</label><div className="w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg font-medium">{remainingAmount > 0 ? <span className="text-red-500">{formatTRY(remainingAmount)}</span> : <span className="text-emerald-600">—</span>}</div></div>
          {form.paymentType === 'Taksitli' && (
            <>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Taksit</label><select value={form.installmentCount} onChange={e => setForm({ ...form, installmentCount: parseInt(e.target.value) })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">{[2,3,4,5,6,8,10,12].map(n => <option key={n} value={n}>{n} Ay</option>)}</select></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">İlk Ödeme</label><input type="date" value={form.firstPaymentDate} onChange={e => setForm({ ...form, firstPaymentDate: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            </>
          )}
        </div>

        {/* Bölüm 6: Taksit Planı */}
        {form.paymentType === 'Taksitli' && (
          <div className="col-span-full border-t border-background-200 pt-4 mt-2">
            <div className="flex items-center gap-2 mb-4"><span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center"><i className="ri-calendar-schedule-line text-background-50 text-sm"></i></span><h3 className="text-sm font-semibold text-foreground-900">Taksit Planı</h3></div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-500 mb-2">Taksit Planı Türü</label>
              <div className="flex gap-2">
                {(['equal', 'custom'] as const).map(t => (
                  <button key={t} onClick={() => setInstallmentPlanType(t)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${installmentPlanType === t ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:bg-background-200'}`}>
                    {t === 'equal' ? 'Eşit Taksit' : 'Özel Taksit Planı'}
                  </button>
                ))}
              </div>
            </div>
            {installmentPlanType === 'equal' && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Taksit</label><select value={form.installmentCount} onChange={e => setForm({ ...form, installmentCount: parseInt(e.target.value) })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">{[2,3,4,5,6,8,10,12].map(n => <option key={n} value={n}>{n} Ay</option>)}</select></div>
                <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">İlk Ödeme</label><input type="date" value={form.firstPaymentDate} onChange={e => setForm({ ...form, firstPaymentDate: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
                {remainingAmount > 0 && form.installmentCount > 0 && <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Aylık Taksit</label><div className="w-full px-3 py-2.5 text-sm bg-accent-50 border border-accent-200 rounded-lg font-medium text-accent-800">{formatTRY(Math.ceil(remainingAmount / form.installmentCount))}</div></div>}
              </div>
            )}
            {installmentPlanType === 'custom' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={addCustomRow} className="px-2.5 py-1.5 text-xs rounded-lg bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium cursor-pointer whitespace-nowrap transition-colors"><i className="ri-add-line mr-1"></i>Taksit Ekle</button>
                  <button type="button" onClick={() => setShowGroupAdd(true)} className="px-2.5 py-1.5 text-xs rounded-lg bg-accent-50 text-accent-600 hover:bg-accent-100 font-medium cursor-pointer whitespace-nowrap transition-colors"><i className="ri-stack-line mr-1"></i>Grup Ekle</button>
                  <button type="button" onClick={distributeEqually} disabled={customInstallments.length === 0 || targetAmount <= 0} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40"><i className="ri-equalizer-line mr-1"></i>Eşit Dağıt</button>
                  <button type="button" onClick={copyPreviousRow} disabled={customInstallments.length === 0} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40"><i className="ri-file-copy-line mr-1"></i>Öncekini Kopyala</button>
                  <button type="button" onClick={generateDates} disabled={customInstallments.length === 0} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40"><i className="ri-calendar-line mr-1"></i>Tarihleri Aylık Oluştur</button>
                  <button type="button" onClick={clearAmounts} disabled={customInstallments.length === 0} className="px-2.5 py-1.5 text-xs rounded-lg bg-background-100 hover:bg-background-200 text-foreground-600 font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40"><i className="ri-eraser-line mr-1"></i>Tutarları Temizle</button>
                  <button type="button" onClick={resetPlan} disabled={customInstallments.length === 0} className="px-2.5 py-1.5 text-xs rounded-lg bg-red-50 text-red-500 hover:bg-red-100 font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-40"><i className="ri-refresh-line mr-1"></i>Planı Sıfırla</button>
                </div>
                {customInstallments.length > 0 && (
                  <div className="overflow-x-auto border border-background-200/70 rounded-xl">
                    <table className="w-full min-w-[600px]">
                      <thead><tr className="border-b border-background-100 bg-background-100/50"><th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">#</th><th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">Vade Tarihi</th><th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">Tutar (TL)</th><th className="px-3 py-2 text-left text-xs font-medium text-foreground-400">Açıklama</th><th className="px-3 py-2 text-center text-xs font-medium text-foreground-400 w-10"></th></tr></thead>
                      <tbody>
                        {customInstallments.map((row) => (
                          <tr key={row.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                            <td className="px-3 py-2 text-sm text-foreground-400 font-medium">{row.sequence_no}</td>
                            <td className="px-3 py-1.5"><input type="date" value={row.due_date} onChange={e => updateCustomRow(row.id, 'due_date', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-background-50 border border-background-200 rounded text-foreground-700" /></td>
                            <td className="px-3 py-1.5"><CurrencyInput value={String(row.amount || '')} onChange={v => updateCustomRow(row.id, 'amount', v)} className="w-28 px-2 py-1.5 text-xs bg-background-50 border border-background-200 rounded text-foreground-700 text-right" /></td>
                            <td className="px-3 py-1.5"><input type="text" value={row.description} onChange={e => updateCustomRow(row.id, 'description', e.target.value)} className="w-full px-2 py-1.5 text-xs bg-background-50 border border-background-200 rounded text-foreground-700" placeholder="Opsiyonel" /></td>
                            <td className="px-3 py-1.5 text-center"><button onClick={() => removeCustomRow(row.id)} className="w-6 h-6 rounded flex items-center justify-center cursor-pointer text-red-400 hover:bg-red-50"><i className="ri-close-line text-sm"></i></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="bg-background-100/50 rounded-xl p-4 border border-background-200/70 space-y-2">
                  <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">Satış Toplamı</span><span className="font-medium">{formatTRY(totalAmount)}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">Peşinat</span><span className="font-medium">{formatTRY(downPayment)}</span></div>
                  <div className="flex items-center justify-between text-sm font-medium"><span className="text-foreground-600">Taksitlendirilecek Tutar</span><span className="text-foreground-800">{formatTRY(targetAmount)}</span></div>
                  <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">Girilen Taksitler Toplamı</span><span className={`font-medium ${customIsBalanced ? 'text-emerald-600' : 'text-red-500'}`}>{formatTRY(customTotal)}</span></div>
                  <div className="flex items-center justify-between text-sm border-t border-background-200 pt-2">
                    <span className="text-foreground-500">{customDiff > 0 ? 'Eksik' : customDiff < 0 ? 'Fazla' : 'Durum'}</span>
                    {customIsBalanced ? (
                      <span className="text-emerald-600 font-medium flex items-center gap-1"><i className="ri-check-line"></i>Plan dengeli</span>
                    ) : (
                      <span className="text-red-500 font-medium">{formatTRY(Math.abs(customDiff))}</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-sm"><span className="text-foreground-500">Taksit Sayısı</span><span className="font-medium">{customInstallments.length}</span></div>
                  {!customIsBalanced && customInstallments.length > 0 && (
                    <button type="button" onClick={addDiffToLast} className="w-full mt-2 px-3 py-2 text-xs rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 font-medium cursor-pointer transition-colors">
                      <i className="ri-arrow-right-line mr-1"></i>Farkı Son Taksite Ekle ({customDiff > 0 ? '+' : ''}{formatTRY(Math.abs(customDiff))})
                    </button>
                  )}
                </div>
                {customInstallments.length > 0 && customIsBalanced && (
                  <div className="bg-emerald-50/50 border border-emerald-200/70 rounded-xl p-4">
                    <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2"><i className="ri-eye-line"></i>Taksit Planı Ön İzleme</h4>
                    <div className="space-y-1.5">
                      {customInstallments.map(row => (
                        <div key={row.id} className="flex items-center justify-between text-sm py-1.5 border-b border-emerald-100 last:border-0">
                          <span className="text-foreground-500">{row.sequence_no}. Taksit · {row.due_date ? new Date(row.due_date).toLocaleDateString('tr-TR') : '—'}</span>
                          <span className="font-medium text-foreground-800">{formatTRY(row.amount)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-sm mt-3 pt-3 border-t border-emerald-200 font-semibold"><span>Toplam</span><span className="text-emerald-700">{formatTRY(customTotal)}</span></div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Bölüm 7: Taksit Güncelleme Stratejisi */}
        {form.paymentType === 'Taksitli' && hasOpenInstallments && priceChanged && (
          <div className="bg-accent-50 border border-accent-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3"><i className="ri-settings-3-line text-accent-600"></i><span className="text-sm font-semibold text-accent-800">Taksit Güncelleme Stratejisi</span></div>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${installmentStrategy === 'last_installment' ? 'bg-accent-100 border border-accent-300' : 'bg-background-50 border border-background-200'}`}>
                <input type="radio" name="instStrategy" checked={installmentStrategy === 'last_installment'} onChange={() => setInstallmentStrategy('last_installment')} className="mt-0.5 accent-accent-500" />
                <div><span className="text-sm font-medium text-foreground-700">Son taksite uygula</span><p className="text-xs text-foreground-400 mt-0.5">Fark yalnızca son açık taksite eklenir veya düşülür.</p></div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${installmentStrategy === 'distribute_evenly' ? 'bg-accent-100 border border-accent-300' : 'bg-background-50 border border-background-200'}`}>
                <input type="radio" name="instStrategy" checked={installmentStrategy === 'distribute_evenly'} onChange={() => setInstallmentStrategy('distribute_evenly')} className="mt-0.5 accent-accent-500" />
                <div><span className="text-sm font-medium text-foreground-700">Açık taksitlere eşit dağıt</span><p className="text-xs text-foreground-400 mt-0.5">Fark yalnızca ödenmemiş taksitlere dağıtılır.</p></div>
              </label>
              {!hasPaidInstallments && (
                <label className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${installmentStrategy === 'recreate' ? 'bg-accent-100 border border-accent-300' : 'bg-background-50 border border-background-200'}`}>
                  <input type="radio" name="instStrategy" checked={installmentStrategy === 'recreate'} onChange={() => setInstallmentStrategy('recreate')} className="mt-0.5 accent-accent-500" />
                  <div><span className="text-sm font-medium text-foreground-700">Taksit planını yeniden oluştur</span><p className="text-xs text-foreground-400 mt-0.5">Yalnızca hiç ödeme alınmamışsa kullanılabilir.</p></div>
                </label>
              )}
            </div>
          </div>
        )}

        {/* Bölüm 8: Bağlı Senetler */}
        {promissoryNotes.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2"><span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center"><i className="ri-file-list-3-line text-background-50 text-sm"></i></span><span className="text-sm font-semibold text-amber-800">Bağlı Senetler</span></div>
            {promissoryNotes.map((n: any) => (
              <div key={n.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-amber-700 bg-amber-100/50 rounded-lg px-3 py-2 mb-1.5 last:mb-0">
                <span className="font-medium">{n.note_no}</span>
                <span>Tutar: {formatTRY(n.amount)}</span>
                <span>Kalan: {formatTRY(n.remaining_amount)}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${n.status === 'Bekliyor' ? 'bg-amber-200 text-amber-800' : n.status === 'Gecikti' ? 'bg-red-200 text-red-800' : 'bg-emerald-200 text-emerald-800'}`}>{n.status}</span>
                {priceChanged && <span className="text-amber-800 font-medium ml-auto">(Satış değişikliği senet bakiyesine yansıtılacak)</span>}
              </div>
            ))}
          </div>
        )}

        {/* Bölüm 9: Teslimat */}
        <div className="border-t border-background-200 pt-4">
          <div className="flex items-center gap-2 mb-4"><span className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center"><i className="ri-truck-line text-background-50 text-sm"></i></span><h3 className="text-sm font-semibold text-foreground-900">Teslimat Bilgileri</h3></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Teslimat Tarihi <span className="text-red-400">*</span></label><input type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Başlangıç Saati <span className="text-red-400">*</span></label><input type="time" value={deliveryStartTime} onChange={e => setDeliveryStartTime(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Bitiş Saati</label><input type="time" value={deliveryEndTime} onChange={e => setDeliveryEndTime(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Nakliyeci</label><input type="text" value={deliveryPerson} onChange={e => setDeliveryPerson(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="Mehmet Nakliye" /></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Araç Plakası</label><input type="text" value={vehiclePlate} onChange={e => setVehiclePlate(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" placeholder="34 ABC 123" /></div>
            <div className="sm:col-span-2 lg:col-span-3"><CityDistrictSelect city={deliveryCity} district={deliveryDistrict} neighborhood={deliveryNeighborhood} onCityChange={setDeliveryCity} onDistrictChange={setDeliveryDistrict} onNeighborhoodChange={setDeliveryNeighborhood} cityLabel="Şehir" selectClassName="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer" /></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1.5">Teslimat Adresi <span className="text-red-400">*</span></label><textarea value={form.deliveryAddress} onChange={e => setForm({ ...form, deliveryAddress: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2}></textarea></div>
            <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Garanti</label><select value={form.warrantyPeriod} onChange={e => setForm({ ...form, warrantyPeriod: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer">{['1 Yıl', '2 Yıl', '3 Yıl', '5 Yıl', '10 Yıl'].map(w => <option key={w} value={w}>{w}</option>)}</select></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1.5">Teslimat Notu</label><textarea value={deliveryNotes} onChange={e => setDeliveryNotes(e.target.value)} maxLength={500} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2}></textarea></div>
            <div className="sm:col-span-2"><label className="block text-xs font-medium text-foreground-500 mb-1.5">Satış Notu</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2}></textarea></div>
          </div>
        </div>

        {/* Butonlar */}
        <div className="flex items-center gap-3 pt-2 justify-end">
          <button onClick={() => navigate(`/sales/${sale.id}`)} className="btn-outline cursor-pointer">İptal</button>
          <button onClick={handleReviewChanges} className="btn-primary flex items-center gap-2 cursor-pointer"><i className="ri-eye-line"></i>Değişiklikleri İncele</button>
        </div>
      </div>

      {/* Değişiklik Özeti Modal */}
      {showChangeSummary && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowChangeSummary(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center"><i className="ri-scales-3-line text-accent-600 text-lg"></i></span>
              <div><h3 className="text-lg font-semibold text-foreground-900">Değişiklik Özeti</h3><p className="text-xs text-foreground-400">{sale.sale_no} düzenleme onayı</p></div>
            </div>

            <div className="space-y-3 mb-5">
              {priceChanged && (
                <div className="bg-background-100/70 rounded-xl p-4 space-y-2">
                  <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Satış Tutarı Değişikliği</h4>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><span className="text-foreground-400">Eski Toplam</span><p className="font-semibold text-foreground-700">{formatTRY(oldTotalAmount)}</p></div>
                    <div><span className="text-foreground-400">Yeni Toplam</span><p className="font-semibold text-foreground-700">{formatTRY(totalAmount)}</p></div>
                    <div><span className="text-foreground-400">Fark</span><p className={`font-bold ${totalAmountDelta > 0 ? 'text-emerald-600' : totalAmountDelta < 0 ? 'text-red-500' : ''}`}>{totalAmountDelta > 0 ? '+' : ''}{formatTRY(totalAmountDelta)}</p></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-sm pt-2 border-t border-background-200">
                    <div><span className="text-foreground-400">Eski Kalan</span><p className="font-semibold text-foreground-700">{formatTRY(oldRemainingAmount)}</p></div>
                    <div><span className="text-foreground-400">Yeni Kalan</span><p className="font-semibold text-foreground-700">{formatTRY(remainingAmount)}</p></div>
                    <div><span className="text-foreground-400">Borç Değişimi</span><p className={`font-bold ${(remainingAmount - oldRemainingAmount) > 0 ? 'text-red-500' : (remainingAmount - oldRemainingAmount) < 0 ? 'text-emerald-600' : ''}`}>{(remainingAmount - oldRemainingAmount) > 0 ? '+' : ''}{formatTRY(remainingAmount - oldRemainingAmount)}</p></div>
                  </div>
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-sm text-primary-700 mt-2">
                    <i className="ri-information-line mr-1"></i>
                    Bu değişiklik müşterinin kalan borcunu <strong>{(remainingAmount - oldRemainingAmount) > 0 ? '+' : ''}{formatTRY(remainingAmount - oldRemainingAmount)} {remainingAmount >= oldRemainingAmount ? 'artıracak' : 'azaltacak'}</strong>tır.
                    {promissoryNotes.length > 0 && ' Bağlı senet bakiyesi de güncellenecek.'}
                  </div>
                </div>
              )}
              {!priceChanged && (
                <div className="bg-background-100/70 rounded-xl p-4 text-center text-sm text-foreground-400">
                  <i className="ri-information-line mr-1"></i>Finansal bir değişiklik tespit edilmedi. Diğer alan güncellemeleri uygulanacak.
                </div>
              )}
            </div>

            <div className="mb-5">
              <label className="block text-xs font-medium text-foreground-500 mb-1.5">Değişiklik Sebebi <span className="text-red-400">*</span></label>
              <textarea value={editReason} onChange={e => setEditReason(e.target.value)} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 resize-none" rows={2} maxLength={500} placeholder="Örn: Ürün değişikliği, fiyat güncellemesi, müşteri talebi..."></textarea>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowChangeSummary(false)} className="flex-1 btn-outline cursor-pointer">Vazgeç</button>
              <button onClick={handleSubmit} disabled={saving || !editReason.trim()} className="flex-1 btn-primary flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">{saving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i>Onayla ve Güncelle</>}</button>
            </div>
          </div>
        </div>
      )}

      {/* Kategori Seçici Modal */}
      {showCategoryPicker && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowCategoryPicker(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="text-center mb-5"><span className="w-12 h-12 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-3"><i className="ri-stack-line text-primary-500 text-xl"></i></span><h3 className="text-lg font-semibold text-foreground-900">Eklenecek ürün kategorisini seçin</h3></div>
            <div className="space-y-2">
              <button onClick={() => addItem('furniture')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-200 bg-amber-50/50 hover:bg-amber-50 cursor-pointer transition-colors text-left group"><span className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0"><i className="ri-sofa-line text-background-50"></i></span><div className="flex-1"><p className="text-sm font-medium text-foreground-800">Mobilya Ekle</p><p className="text-xs text-foreground-400">Koltuk, yatak odası, yemek odası...</p></div><i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-foreground-500"></i></button>
              <button onClick={() => addItem('profilo')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-sky-200 bg-sky-50/50 hover:bg-sky-50 cursor-pointer transition-colors text-left group"><span className="w-9 h-9 rounded-lg bg-sky-500 flex items-center justify-center flex-shrink-0"><i className="ri-fridge-line text-background-50"></i></span><div className="flex-1"><p className="text-sm font-medium text-foreground-800">Profilo Ekle</p><p className="text-xs text-foreground-400">Beyaz eşya, klima, ankastre...</p></div><i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-foreground-500"></i></button>
              <button onClick={() => addItem('other')} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-foreground-200 bg-background-100/50 hover:bg-background-100 cursor-pointer transition-colors text-left group"><span className="w-9 h-9 rounded-lg bg-foreground-500 flex items-center justify-center flex-shrink-0"><i className="ri-more-line text-background-50"></i></span><div className="flex-1"><p className="text-sm font-medium text-foreground-800">Diğer Ürün Ekle</p><p className="text-xs text-foreground-400">Avize, perde, halı, dekorasyon...</p></div><i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-foreground-500"></i></button>
            </div>
            <button onClick={() => setShowCategoryPicker(false)} className="w-full mt-3 px-4 py-2.5 text-sm text-foreground-500 hover:text-foreground-700 cursor-pointer rounded-lg hover:bg-background-100 transition-colors">Vazgeç</button>
          </div>
        </div>
      )}

      {/* Grup Ekle Modal */}
      {showGroupAdd && (
        <div className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center p-4" onClick={() => setShowGroupAdd(false)}>
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5"><h3 className="text-lg font-semibold text-foreground-900">Grup Ekle</h3><button onClick={() => setShowGroupAdd(false)} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-foreground-500"></i></button></div>
            <div className="space-y-4">
              <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Kaç Ay?</label><input type="number" min="1" value={groupForm.months} onChange={e => setGroupForm({ ...groupForm, months: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Aylık Tutar (TL)</label><input type="number" min="0" value={groupForm.monthlyAmount || ''} onChange={e => setGroupForm({ ...groupForm, monthlyAmount: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">İlk Vade Tarihi</label><input type="date" value={groupForm.firstDueDate} onChange={e => setGroupForm({ ...groupForm, firstDueDate: e.target.value })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700" /></div>
              <div><label className="block text-xs font-medium text-foreground-500 mb-1.5">Tekrar Aralığı</label><select value={groupForm.interval} onChange={e => setGroupForm({ ...groupForm, interval: e.target.value as any })} className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer"><option value="Aylık">Aylık</option><option value="15 Gün">15 Günde Bir</option><option value="Haftalık">Haftalık</option><option value="Özel">Özel Tarih</option></select></div>
              <button onClick={handleGroupAdd} disabled={groupForm.months < 1 || groupForm.monthlyAmount <= 0 || !groupForm.firstDueDate} className="w-full btn-primary text-center cursor-pointer disabled:opacity-40"><i className="ri-check-line mr-1.5"></i>Planı Oluştur</button>
            </div>
          </div>
        </div>
      )}

      <CustomerFormModal isOpen={showCustomerModal} onClose={() => setShowCustomerModal(false)} onCreated={handleCustomerCreated} />
    </div>
  );
}